import React, { useEffect, useMemo, useState } from "react";
import { styled } from "@mui/system";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";

import ErrorMessage from "../ErrorMessage/ErrorMessage";
import Select from "../Select/Select";
import MenuItem from "../MenuItem/CustomMenuItem";

const StyledContainer = styled("div")({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: "6px",
});

const Placeholder = styled("span")({
  color: "#9ca3af",
  fontSize: "0.9rem",
});

const SingleSelect = ({
  id,
  name,
  value,
  tag,
  placeholder,
  handleChange,
  menuItems = [],
  errorMessage,
  disabled = false,
  required = false,
}) => {
  const [open, setOpen] = useState(false);

  const valueLabelMap = useMemo(() => {
    const map = new Map();
    menuItems?.forEach((item) => {
      map.set(item.value, item.label);
    });
    return map;
  }, [menuItems]);

  /** ✅ Auto-select if only one enabled item */
  useEffect(() => {
    if (disabled || value) return;

    const enabledItems = menuItems.filter(item => !item.disabled);

    if (enabledItems.length === 1) {
      handleChange({
        target: {
          name,
          value: enabledItems[0].value,
        },
      });
    }
  }, [menuItems, value, disabled, handleChange, name]);

  const hasValue = Boolean(value);

  const renderValue = (selected) => {
    if (!selected || !valueLabelMap.has(selected)) {
      return <Placeholder>{placeholder}</Placeholder>;
    }
    return valueLabelMap.get(selected);
  };

  return (
    <StyledContainer>
      <FormControl
        fullWidth
        variant="outlined"
        disabled={disabled}
        error={Boolean(errorMessage)}
      >
        <InputLabel
          id={`${id}-label`}
          required={required}
          shrink={hasValue || open}
        >
          {tag}
        </InputLabel>

        <Select
          id={id}
          name={name}
          labelId={`${id}-label`}
          value={value ?? ""}
          displayEmpty
          onChange={handleChange}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          input={(hasValue || open) ? <OutlinedInput label={tag} /> : null}
          renderValue={renderValue}
          sx={{
            "& .MuiSelect-select": {
              display: "flex",
              alignItems: "center",
              paddingTop: "12px",
              paddingBottom: "12px",
            },
          }}
        >
          {menuItems.map((item) => (
            <MenuItem
              key={item.id ?? item.value}
              value={item.value}
              disabled={item.disabled}
            >
              {item.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </StyledContainer>
  );
};

export default React.memo(SingleSelect);
